<template>
    <v-card v-if="requestProtocol != null">
        <v-card-title>
            <v-row>
                <v-col>
                    {{ requestProtocol.name }}
                </v-col>
                <v-spacer />
                <v-col class="text-right">
                    <v-btn color="primary" @click="showEditProtocolForm" class="mr-3"
                        >Edit Protocol</v-btn
                    >
                    <v-btn color="primary" @click="showAddAttributeForm">Add Data Attribute</v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-card-subtitle>
            <strong>Request ID:</strong> {{ requestProtocol.code }}
            <br />
            <strong>Processor:</strong> {{ requestProcessorName }}
            <br />
            <strong>Default Source Samples Container Type:</strong>
            {{ materialSamplesContainerType }}
            <br />
            <strong>Default Request Processing Container Type:</strong>
            {{ requestProcessingContainerType }}
        </v-card-subtitle>
        <v-card-text>
            <schema-attributes-list
                v-if="requestProtocol.attributes"
                :attributes="requestProtocol.attributes"
                :is-registry-schema="true"
                :allow-update="userCanManageRequestSchema"
                @showEditAttributeForm="showEditAttributeForm"
                @deleteAttribute="deleteAttribute"
            />
        </v-card-text>

        <v-dialog v-if="editAttribute" v-model="editAttributeFormVisible" persistent>
            <schema-attribute-form
                :all-registry-schemas="allRegistrySchemas"
                :attribute="editAttribute"
                :thing-schema="requestProtocol"
                :isNewAttribute="isNewAttribute"
                :is-registry-schema="true"
                @closeEditAttributeForm="closeEditAttributeForm"
                @saveAttribute="saveAttribute"
            />
        </v-dialog>

        <v-dialog v-model="requestProtocolDialogVisible" max-width="600px">
            <request-protocol-form
                :existingProtocol="requestProtocol"
                :requestProcessors="requestProcessors"
                :containerTypes="containerTypes"
                @canceled="requestProtocolDialogVisible = false"
                @requestProtocolSaved="handleRequestProtocolSaved"
            />
        </v-dialog>
    </v-card>
</template>

<script setup lang="ts">
import { useRoute } from "vue-router";
import { useAuth } from "@/lib/ts/auth";
import { ref, onMounted, computed } from "vue";
import { withLoadingScreen } from "@/lib/loading.js";
import { RequestProcessor, samplesAPI, SavedRequestProtocol } from "@/services/samples/samplesAPI";
import { ContainerType } from "@/services/commonModels";
import RequestProtocolForm from "@/components/SamplesV2/RequestProtocolForm.vue";
import { useSchemaAttributeForm } from "@/components/SchemaConfig/schemaAttributeFormComposable";
import SchemaAttributeForm from "../SchemaConfig/SchemaAttributeForm.vue";
import SchemaAttributesList, { AttributeItem } from "../SchemaConfig/SchemaAttributesList.vue";
import { Attribute } from "@/services/things/models";
import { upsert } from "@/lib/arrayUtils";

const route = useRoute();
const auth = useAuth();

const requestProtocolCode = ref<string>(route.params.code as string);
const requestProtocol = ref<SavedRequestProtocol>();
const requestProtocolDialogVisible = ref(false);
const containerTypes = ref<ContainerType[]>([]);
const requestProcessors = ref<RequestProcessor[]>([]);

const userCanManageRequestSchema = computed(() => {
    return auth.isAuthorized("MaterialSamples:ManageConfig");
});

const {
    allRegistrySchemas,
    editAttribute,
    editAttributeFormVisible,
    isNewAttribute,
    showAddAttributeForm,
    showEditAttributeForm,
    closeEditAttributeForm,
} = useSchemaAttributeForm();

onMounted(async () => {
    await withLoadingScreen(async () => {
        await reloadProtocol();
        containerTypes.value = await samplesAPI.fetchRequestContainerTypes();
        requestProcessors.value = await samplesAPI.fetchRequestProcessors();
    });
});

async function reloadProtocol() {
    await withLoadingScreen(async () => {
        requestProtocol.value = await samplesAPI.fetchRequestProtocolByCode(
            requestProtocolCode.value
        );
    });
}

async function showEditProtocolForm() {
    requestProtocolDialogVisible.value = true;
}

async function handleRequestProtocolSaved() {
    requestProtocolDialogVisible.value = false;
    await reloadProtocol();
}

async function saveAttribute(updatedAttribute: Attribute) {
    const updatedAttributes = upsert(
        requestProtocol.value!.attributes,
        updatedAttribute,
        (item) => updatedAttribute.name === item.name
    );
    await saveProtocolSchemaAttributes(updatedAttributes);
}

async function saveProtocolSchemaAttributes(updatedAttributes: Attribute[]) {
    const updatedProtocol = {
        ...requestProtocol.value!,
        attributes: updatedAttributes,
    } as SavedRequestProtocol;

    await withLoadingScreen(async () => {
        await samplesAPI.updateRequestProtocol(requestProtocol.value!.id, updatedProtocol);
        closeEditAttributeForm();
        await reloadProtocol();
    });
}

async function deleteAttribute(attributeItem: AttributeItem) {
    if (
        confirm(
            `Warning! Deleting ${attributeItem.name} may result in data loss. Are you sure you want to delete ${attributeItem.name}?`
        )
    ) {
        const currentAttributes = requestProtocol.value!.attributes;
        const updatedAttributes = currentAttributes.filter((currentAttribute) => {
            return currentAttribute.name !== attributeItem.name;
        });

        await saveProtocolSchemaAttributes(updatedAttributes);
    }
}

const requestProcessorName = computed(() => {
    if (!requestProtocol.value) {
        return "";
    }
    const processor = requestProcessors.value.find(
        (proc) => proc.id === requestProtocol.value!.requestProcessorId
    );
    return processor ? processor.name : "Unknown Processor";
});

const materialSamplesContainerType = computed(() => {
    if (!requestProtocol.value) {
        return "";
    }
    return getContainerTypeName(requestProtocol.value.samplesContainerTypeId);
});

const requestProcessingContainerType = computed(() => {
    if (!requestProtocol.value) {
        return "";
    }
    return getContainerTypeName(requestProtocol.value.requestContainerTypeId);
});

function getContainerTypeName(containerTypeId: string | null): string {
    if (!containerTypeId) {
        return "N/A";
    }
    const containerType = containerTypes.value.find((ct) => ct.id === containerTypeId);
    return containerType ? containerType.name : "Unknown Container Type";
}
</script>
